import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Box1, CloseCircle, Danger, Tag2 } from "iconsax-react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { GenericStatCard } from "@/components/Molecules";
import { formatPersianNumber } from "@/lib/format/persian";
import type { ProductStatsRowProps } from "./ProductStatsRow.types";
import * as styles from "./ProductStatsRow.styles";

export type { ProductStatsRowProps } from "./ProductStatsRow.types";

export const ProductStatsRow = memo(function ProductStatsRow({
  stats,
  categoriesCount = 0,
  isLoading = false,
}: ProductStatsRowProps) {
  const { t } = useTranslation("products");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const iconSize = isMobile ? 20 : 25;

  const items = [
    {
      value: formatPersianNumber(stats?.total_count ?? 0),
      label: t("stats.total"),
      icon: <Box1 size={iconSize} variant="Bold" color={theme.palette.primary.main} />,
      accent: theme.palette.primary.main,
    },
    {
      value: formatPersianNumber(categoriesCount),
      label: t("stats.categories"),
      icon: <Tag2 size={iconSize} variant="Bold" color={theme.palette.secondary.main} />,
      accent: theme.palette.secondary.main,
    },
    {
      value: formatPersianNumber(stats?.low_stock_count ?? 0),
      label: t("stats.lowStock"),
      icon: <Danger size={iconSize} variant="Bold" color={theme.palette.warning.main} />,
      accent: theme.palette.warning.main,
      valueColor: theme.palette.warning.main,
    },
    {
      value: formatPersianNumber(stats?.out_of_stock_count ?? 0),
      label: t("stats.outOfStock"),
      icon: <CloseCircle size={iconSize} variant="Bold" color={theme.palette.error.main} />,
      accent: theme.palette.error.main,
      valueColor: theme.palette.error.main,
    },
  ];

  return (
    <Box sx={styles.grid}>
      {items.map((item) => (
        <GenericStatCard
          key={item.label}
          value={item.value}
          label={item.label}
          icon={item.icon}
          accentColor={item.accent}
          valueColor={"valueColor" in item ? item.valueColor : undefined}
          isLoading={isLoading}
        />
      ))}
    </Box>
  );
});
